import { ChangeEvent, useEffect, useRef, useState, KeyboardEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Popover } from 'react-tiny-popover'
import styled, { useTheme } from 'styled-components'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { InputCheckbox } from '@/components/common/inputs'
import { isTypoVariant, Typo, TypoVariant, TypoVariantKeys } from '@/components/common/typography'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { OrderFormType } from '@/containers/hooks/order/order-form-schema'
import { useSetEstimateOrder } from '@/containers/order/set-estimate-order.hook'
import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { GlobalTheme, isGlobalThemeColor } from '@/styles/globalTheme'
import { currencyText } from '@/utils/utilNumber'
import type { EstimateOrderResponseDtoV1MileagePolicyContent } from '@/apis/rest/generated/types'

interface SpecialMileageContent {
  id: number
  type: 'title' | 'body'
  text: string
  font: TypoVariantKeys
  color?: string
}

const removeSeparator = (value: string): string => {
  return value.replace(/,/g, '')
}

const getSpecialMileageContentList = (mileagePolicyContent?: EstimateOrderResponseDtoV1MileagePolicyContent) => {
  const tempSpecialMileageContentList: SpecialMileageContent[] = mileagePolicyContent?.contents
    ? JSON.parse(mileagePolicyContent.contents)
    : []

  return tempSpecialMileageContentList.map((content) => {
    return {
      ...content,
      font: isTypoVariant(content.font) ? TypoVariant[content.font] : TypoVariant.Body4Regular,
      color:
        content.color && isGlobalThemeColor(content.color)
          ? GlobalTheme.color[content.color]
          : GlobalTheme.color.gray900,
    }
  })
}

interface MileageSectionProps {
  availableMileage: number
  mileageDiscountAmount: number
  availableMileagePercentage: number
  isUseAllMileage?: boolean
  mileagePolicyContent?: EstimateOrderResponseDtoV1MileagePolicyContent
}

export const MileageSection = ({
  availableMileage,
  mileageDiscountAmount,
  availableMileagePercentage,
  isUseAllMileage,
  mileagePolicyContent,
}: MileageSectionProps) => {
  const { setValue } = useFormContext<OrderFormType>()
  const { color, iconSize } = useTheme()
  const dispatch = useDispatch()

  const { setEstimateOrderByMileage } = useSetEstimateOrder()

  const [mileage, setMileage] = useState<string>('')

  const [isAllMileageUseCheck, setIsAllMileageUseCheck] = useState<boolean>(!!isUseAllMileage)
  const popoverParentRef = useRef<HTMLDivElement>(null)
  const [isMileagePopoverOpen, setIsMileagePopoverOpen] = useState(false)

  const specialMileageContentList = getSpecialMileageContentList(mileagePolicyContent)

  const setMileageValue = (value: number) => {
    setMileage(value.toLocaleString('ko-KR'))
    setEstimateOrderByMileage(value)
  }

  const handleMileageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const valueWithSeparatorRemoved = removeSeparator(value)
    const numericValue = Number(valueWithSeparatorRemoved)

    // 빈 문자열이 입력될 경우
    if (value.length === 0) {
      setMileageValue(0)
      return
    }

    // 숫자가 아니거나 0 이하일 경우
    if (isNaN(numericValue) || numericValue <= 0) {
      return
    }

    // 사용 가능한 적립금보다 입력한 적립금이 많을 경우
    if (availableMileage > 0 && numericValue > availableMileage) {
      setMileageValue(availableMileage)
      return
    }

    setMileageValue(numericValue)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleMileageInputClick = () => {
    if (!isAllMileageUseCheck) {
      return
    }
    dispatch(
      doSetGlobalToastContent({
        content: '항상 적립금 최대 사용을 해지해 주세요',
      })
    )
  }

  // 전액 사용 버튼 클릭
  const handleClickUseAllMileage = () => {
    setMileageValue(availableMileage)
  }

  // 항상 적립금 전액 사용 체크박스 클릭
  const handleAllMileageUseCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    setIsAllMileageUseCheck(isChecked)
    setValue('isUseAllMileage', isChecked)
    if (isChecked) {
      setMileageValue(availableMileage)
    }
  }

  const handlePopoverToggle = () => {
    setIsMileagePopoverOpen((prevState) => !prevState)
  }

  useEffect(() => {
    setMileageValue(isAllMileageUseCheck ? availableMileage : mileageDiscountAmount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllMileageUseCheck, availableMileage, mileageDiscountAmount])

  return (
    <div ref={popoverParentRef}>
      <StyledSectionTitle>
        <Typo variant="Body1 Bold">적립금</Typo>
      </StyledSectionTitle>
      <StyledMileageContainer>
        <StyledInputContainer>
          <StyledInputWrapper onClick={handleMileageInputClick}>
            <StyledInputContainerInput
              className="Body3 Regular"
              value={mileage}
              onChange={handleMileageChange}
              onKeyDown={handleKeyDown}
              placeholder="0"
              readOnly={isAllMileageUseCheck}
              disabled={!availableMileage}
            />
            <StyledInputContainerUnit className="unit">
              <Typo variant="Body3 Regular">원</Typo>
            </StyledInputContainerUnit>
          </StyledInputWrapper>
          <StyledButton type="button" onClick={handleClickUseAllMileage}>
            <Typo variant="Body1 Bold" color={color.grayWhite}>
              전액 사용
            </Typo>
          </StyledButton>
        </StyledInputContainer>

        <StyledAvailableMileageContainer>
          <Typo as="span" variant="Body3 Regular" color={GlobalColorEnum.Blue500}>
            {currencyText(availableMileage || 0)}
          </Typo>
          <Typo as="span" variant="Body3 Regular">
            사용 가능
          </Typo>
          {popoverParentRef.current && (
            <Popover
              isOpen={isMileagePopoverOpen}
              positions={['bottom']}
              padding={3.5}
              boundaryElement={popoverParentRef.current}
              content={
                <StyledPopoverContent>
                  <Typo variant={TypoVariant.Body3Regular} color={color.gray900}>
                    {`적립금은 결제 금액의 ${availableMileagePercentage}%까지 사용 가능해요`}
                  </Typo>
                  <div onClick={handlePopoverToggle}>
                    <FlatIcon type={IconNameEnum.IcClose} color={color.gray300} size={iconSize.size16} />
                  </div>
                </StyledPopoverContent>
              }
            >
              <div onClick={handlePopoverToggle}>
                <FlatIcon type={IconNameEnum.IcTip} color={color.gray300} size={iconSize.size16} />
              </div>
            </Popover>
          )}
        </StyledAvailableMileageContainer>
        {!!specialMileageContentList.length && (
          <StyledSpecialMileageCard data-cy="special-mileage-card">
            <div>
              <StyledSpecialMileageCardTitle>
                {specialMileageContentList
                  .filter((content) => content.type === 'title')
                  .map(({ id, font, color, text }) => (
                    <Typo key={id} as="span" variant={font} color={color}>
                      {text}{' '}
                    </Typo>
                  ))}
              </StyledSpecialMileageCardTitle>
              <div>
                {specialMileageContentList
                  .filter((content) => content.type === 'body')
                  .map(({ id, font, color, text }) => (
                    <Typo key={id} as="span" variant={font} color={color}>
                      {text}{' '}
                    </Typo>
                  ))}
              </div>
            </div>
            <FlatIcon type={IconNameEnum.IcCoinFlat} size={iconSize.size50} />
          </StyledSpecialMileageCard>
        )}

        <StyledInputCheckboxWrapper>
          <InputCheckbox id="auto_check" checked={isAllMileageUseCheck} onChange={(e) => handleAllMileageUseCheck(e)}>
            <Typo variant="Body2 Regular">항상 적립금 전액 사용</Typo>
          </InputCheckbox>
        </StyledInputCheckboxWrapper>
      </StyledMileageContainer>
    </div>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledInputCheckboxWrapper = styled.div`
  margin-top: 1.6rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

const StyledMileageContainer = styled.div`
  padding: 0 1.6rem 2rem;
`

const StyledInputContainer = styled.div`
  display: flex;
  gap: 0.8rem;
`

const StyledInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center; /* 세로 중앙 정렬 */
  border: 1px solid ${GlobalColorEnum.Gray100};
  border-radius: 0.8rem;
  transition: border-color 0.3s;
  padding: 1rem 1.6rem;
  :focus-within {
    border-color: ${GlobalColorEnum.Blue500};
  }
`

const StyledInputContainerInput = styled.input`
  width: 90%;
  flex: 1; /* 나머지 공간을 모두 차지 */
  text-align: right;
  border: none;
  :focus {
    outline: none;
  }
  :disabled {
    background-color: ${GlobalColorEnum.GrayWhite};
  }
`

const StyledInputContainerUnit = styled.span`
  margin-left: 0;
  pointer-events: none; /* 입력란 클릭 가능하도록 설정 */
`

const StyledButton = styled.button`
  padding: 1.3rem 1.6rem;
  background-color: ${GlobalColorEnum.Blue500};
  border: none;
  border-radius: 0.8rem;
  cursor: pointer;
  :active {
    background-color: ${GlobalColorEnum.Blue600};
  }
`

const StyledAvailableMileageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.8rem;
`

const StyledPopoverContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  z-index: 100;
  padding: 1.6rem;
  margin: 0 1.6rem;
  border: 1px solid ${({ theme }) => theme.color.gray700};
  border-radius: 0.8rem;
  background-color: ${({ theme }) => theme.color.grayWhite};
`

const StyledSpecialMileageCard = styled.div`
  padding: 0.8rem 1.6rem;
  margin-top: 1.2rem;
  border-radius: 0.8rem;
  background-color: ${({
    theme: {
      color: { blue30 },
    },
  }) => blue30};

  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  &::before {
    // 삼각형 rem 사용시 오차발생, 반드시 px 사용
    content: '';
    border-bottom: ${({
      theme: {
        color: { blue30 },
      },
    }) => `8px solid ${blue30}`};
    border-top: 8px solid transparent;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    position: absolute;
    top: -16px;
    left: 8px;
  }
`

const StyledSpecialMileageCardTitle = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
`
